import { useState, useCallback, useRef, useEffect } from 'react';
import { Button } from '../Button/Button';
import { SparkIcon } from '../SparkIcon';
import { DatePickerDay, type DatePickerDayState } from './DatePickerDay';
import { DatePickerPreset } from './DatePickerPreset';
import './DatePicker.css';

export type DatePickerRange = false | true;

/** Calendar menu supporting single date and range selection, with optional presets. Controlled and uncontrolled. */
export type DatePickerMenuProps = {
  /** Set to `true` to show two side-by-side calendars for start/end range picking. */
  range?: DatePickerRange;
  showPreset?: boolean;
  /** Override label for left/single calendar header (computed from state if omitted). */
  monthLabel?: string;
  /** Override label for right calendar header in range mode (computed from state if omitted). */
  secondMonthLabel?: string;
  className?: string;
  /** Currently selected date (single mode, controlled). */
  value?: Date | null;
  /** Initial value for uncontrolled single mode. */
  defaultValue?: Date | null;
  /** Called when a day is selected (single mode). */
  onChange?: (date: Date) => void;
  /** Range start date (controlled). */
  rangeStart?: Date | null;
  /** Range end date (controlled). */
  rangeEnd?: Date | null;
  /** Called when a complete range is selected. */
  onRangeChange?: (start: Date, end: Date) => void;
  /** Earliest selectable date. */
  minDate?: Date;
  /** Latest selectable date. */
  maxDate?: Date;
  /** Called when preset is clicked (label is passed). */
  onPresetSelect?: (preset: string) => void;
  /** Called on Escape key (e.g. close a popover). */
  onEscape?: () => void;
};

type CalendarCell = {
  label: string;
  state?: DatePickerDayState;
  date: Date;
};

const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function isSameDay(a: Date, b: Date | null | undefined): boolean {
  if (!b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isDateBefore(a: Date, b: Date): boolean {
  return a.getTime() < b.getTime();
}

function isDateAfter(a: Date, b: Date): boolean {
  return a.getTime() > b.getTime();
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function buildCalendarGrid(
  year: number,
  month: number,
  options: {
    selectedDate?: Date | null;
    rangeStart?: Date | null;
    rangeEnd?: Date | null;
    minDate?: Date;
    maxDate?: Date;
  },
): CalendarCell[][] {
  const today = new Date();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDayOfWeek = new Date(year, month, 1).getDay();

  const { selectedDate, rangeStart, rangeEnd, minDate, maxDate } = options;

  const cells: CalendarCell[] = [];

  const prevMonth = month === 0 ? 11 : month - 1;
  const prevYear = month === 0 ? year - 1 : year;
  const daysInPrevMonth = getDaysInMonth(prevYear, prevMonth);
  for (let i = firstDayOfWeek - 1; i >= 0; i--) {
    const day = daysInPrevMonth - i;
    cells.push({
      label: String(day),
      state: 'inactive',
      date: new Date(prevYear, prevMonth, day),
    });
  }

  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day);
    let state: DatePickerDayState = 'default';

    if (minDate && isDateBefore(startOfDay(date), startOfDay(minDate))) {
      state = 'disabled';
    } else if (maxDate && isDateAfter(startOfDay(date), startOfDay(maxDate))) {
      state = 'disabled';
    } else if (rangeStart && rangeEnd) {
      if (isSameDay(date, rangeStart)) {
        state = 'in-range-start';
      } else if (isSameDay(date, rangeEnd)) {
        state = 'in-range-end';
      } else if (
        isDateAfter(startOfDay(date), startOfDay(rangeStart)) &&
        isDateBefore(startOfDay(date), startOfDay(rangeEnd))
      ) {
        state = 'in-range-middle';
      }
    } else if (selectedDate && isSameDay(date, selectedDate)) {
      state = 'selected';
    }

    if (state === 'default' && isSameDay(date, today)) {
      state = 'current';
    }

    cells.push({ label: String(day), state, date });
  }

  const totalCells = Math.ceil(cells.length / 7) * 7;
  const nextMonth = month === 11 ? 0 : month + 1;
  const nextYear = month === 11 ? year + 1 : year;
  let nextDay = 1;
  while (cells.length < totalCells) {
    cells.push({
      label: String(nextDay),
      state: 'inactive',
      date: new Date(nextYear, nextMonth, nextDay),
    });
    nextDay++;
  }

  const rows: CalendarCell[][] = [];
  for (let i = 0; i < cells.length; i += 7) {
    rows.push(cells.slice(i, i + 7));
  }
  return rows;
}

function NavButtons({
  direction,
  onPrevMonth,
  onPrevYear,
  onNextMonth,
  onNextYear,
}: {
  direction: 'leading' | 'trailing';
  onPrevMonth?: () => void;
  onPrevYear?: () => void;
  onNextMonth?: () => void;
  onNextYear?: () => void;
}) {
  if (direction === 'leading') {
    return (
      <div className="spark-date-picker-nav">
        <Button
          variant="ghost"
          size="small"
          className="spark-date-picker-icon-btn"
          aria-label="Previous year"
          icon={<SparkIcon slug="chevrons-left" size="small" />}
          onClick={onPrevYear}
        />
        <Button
          variant="ghost"
          size="small"
          className="spark-date-picker-icon-btn"
          aria-label="Previous month"
          icon={<SparkIcon slug="chevron-left" size="small" />}
          onClick={onPrevMonth}
        />
      </div>
    );
  }

  return (
    <div className="spark-date-picker-nav">
      <Button
        variant="ghost"
        size="small"
        className="spark-date-picker-icon-btn"
        aria-label="Next month"
        icon={<SparkIcon slug="chevron-right" size="small" />}
        onClick={onNextMonth}
      />
      <Button
        variant="ghost"
        size="small"
        className="spark-date-picker-icon-btn"
        aria-label="Next year"
        icon={<SparkIcon slug="chevrons-right" size="small" />}
        onClick={onNextYear}
      />
    </div>
  );
}

function CalendarMonth({
  monthLabel,
  rows,
  showTrailingNav = true,
  showLeadingNav = true,
  onDayClick,
  onPrevMonth,
  onPrevYear,
  onNextMonth,
  onNextYear,
  onKeyDown,
  gridRef,
}: {
  monthLabel: string;
  rows: CalendarCell[][];
  showTrailingNav?: boolean;
  showLeadingNav?: boolean;
  onDayClick?: (date: Date) => void;
  onPrevMonth?: () => void;
  onPrevYear?: () => void;
  onNextMonth?: () => void;
  onNextYear?: () => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  gridRef?: React.RefObject<HTMLDivElement>;
}) {
  return (
    <div className="spark-date-picker-month">
      <div className="spark-date-picker-header">
        {showLeadingNav ? (
          <NavButtons
            direction="leading"
            onPrevMonth={onPrevMonth}
            onPrevYear={onPrevYear}
          />
        ) : (
          <div className="spark-date-picker-nav" aria-hidden="true" />
        )}
        <p className="spark-date-picker-month-label">{monthLabel}</p>
        {showTrailingNav ? (
          <NavButtons
            direction="trailing"
            onNextMonth={onNextMonth}
            onNextYear={onNextYear}
          />
        ) : (
          <div className="spark-date-picker-nav" aria-hidden="true" />
        )}
      </div>
      <div
        className="spark-date-picker-grid"
        role="grid"
        aria-label={monthLabel}
        onKeyDown={onKeyDown}
        ref={gridRef}
      >
        <div className="spark-date-picker-row" role="row">
          {WEEKDAY_LABELS.map((day) => (
            <div key={day} className="spark-date-picker-cell-wrap" role="columnheader">
              <DatePickerDay label={day} interactive={false} />
            </div>
          ))}
        </div>
        {rows.map((row, rowIndex) => (
          <div key={rowIndex} className="spark-date-picker-row" role="row">
            {row.map((cell, colIndex) => (
              <div
                key={`${rowIndex}-${colIndex}-${cell.label}`}
                className="spark-date-picker-cell-wrap"
                role="gridcell"
              >
                <DatePickerDay
                  label={cell.label}
                  state={cell.state}
                  onClick={
                    cell.state !== 'inactive' && cell.state !== 'disabled'
                      ? () => onDayClick?.(cell.date)
                      : undefined
                  }
                  tabIndex={cell.state === 'inactive' || cell.state === 'disabled' ? -1 : 0}
                />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function DatePickerMenu({
  range = false,
  showPreset = true,
  monthLabel: monthLabelOverride,
  secondMonthLabel: secondMonthLabelOverride,
  className = '',
  value,
  defaultValue,
  onChange,
  rangeStart: rangeStartProp,
  rangeEnd: rangeEndProp,
  onRangeChange,
  minDate,
  maxDate,
  onPresetSelect,
  onEscape,
}: DatePickerMenuProps) {
  const initialDate = value ?? defaultValue ?? new Date();
  const [displayMonth, setDisplayMonth] = useState(initialDate.getMonth());
  const [displayYear, setDisplayYear] = useState(initialDate.getFullYear());
  const [selectedDate, setSelectedDate] = useState<Date | null>(value ?? defaultValue ?? null);
  const [rangeStart, setRangeStart] = useState<Date | null>(rangeStartProp ?? null);
  const [rangeEnd, setRangeEnd] = useState<Date | null>(rangeEndProp ?? null);
  const [rangeSelecting, setRangeSelecting] = useState(false);

  const gridRef = useRef<HTMLDivElement>(null!);

  const controlledSelected = value !== undefined ? value : selectedDate;
  const controlledRangeStart = rangeStartProp !== undefined ? rangeStartProp : rangeStart;
  const controlledRangeEnd = rangeEndProp !== undefined ? rangeEndProp : rangeEnd;

  useEffect(() => {
    if (value !== undefined && value) {
      setDisplayMonth(value.getMonth());
      setDisplayYear(value.getFullYear());
    }
  }, [value]);

  const goToPrevMonth = useCallback(() => {
    setDisplayMonth((m) => {
      if (m === 0) {
        setDisplayYear((y) => y - 1);
        return 11;
      }
      return m - 1;
    });
  }, []);

  const goToNextMonth = useCallback(() => {
    setDisplayMonth((m) => {
      if (m === 11) {
        setDisplayYear((y) => y + 1);
        return 0;
      }
      return m + 1;
    });
  }, []);

  const goToPrevYear = useCallback(() => {
    setDisplayYear((y) => y - 1);
  }, []);

  const goToNextYear = useCallback(() => {
    setDisplayYear((y) => y + 1);
  }, []);

  const handleDayClick = useCallback(
    (date: Date) => {
      if (range) {
        if (!rangeSelecting || !controlledRangeStart) {
          setRangeStart(date);
          setRangeEnd(null);
          setRangeSelecting(true);
        } else {
          const start = controlledRangeStart;
          let finalStart: Date;
          let finalEnd: Date;
          if (isDateBefore(date, start)) {
            finalStart = date;
            finalEnd = start;
          } else {
            finalStart = start;
            finalEnd = date;
          }
          setRangeStart(finalStart);
          setRangeEnd(finalEnd);
          setRangeSelecting(false);
          onRangeChange?.(finalStart, finalEnd);
        }
      } else {
        setSelectedDate(date);
        onChange?.(date);
      }
    },
    [range, rangeSelecting, controlledRangeStart, onChange, onRangeChange],
  );

  const handleGoToToday = useCallback(() => {
    const today = new Date();
    setDisplayMonth(today.getMonth());
    setDisplayYear(today.getFullYear());
    if (!range) {
      setSelectedDate(today);
      onChange?.(today);
    }
  }, [range, onChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        onEscape?.();
        return;
      }

      const target = e.target as HTMLElement;
      if (!target.classList.contains('spark-date-picker-day')) return;

      const grid = gridRef.current;
      if (!grid) return;

      const buttons = Array.from(
        grid.querySelectorAll<HTMLButtonElement>('button.spark-date-picker-day'),
      );
      const currentIndex = buttons.indexOf(target as HTMLButtonElement);
      if (currentIndex === -1) return;

      let nextIndex = currentIndex;
      switch (e.key) {
        case 'ArrowLeft':
          nextIndex = currentIndex - 1;
          break;
        case 'ArrowRight':
          nextIndex = currentIndex + 1;
          break;
        case 'ArrowUp':
          nextIndex = currentIndex - 7;
          break;
        case 'ArrowDown':
          nextIndex = currentIndex + 7;
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          target.click();
          return;
        default:
          return;
      }

      e.preventDefault();
      if (nextIndex >= 0 && nextIndex < buttons.length) {
        buttons[nextIndex].focus();
      }
    },
    [onEscape],
  );

  const computedMonthLabel = `${MONTH_NAMES[displayMonth]} ${displayYear}`;

  let secondMonth = displayMonth + 1;
  let secondYear = displayYear;
  if (secondMonth > 11) {
    secondMonth = 0;
    secondYear = displayYear + 1;
  }
  const computedSecondMonthLabel = `${MONTH_NAMES[secondMonth]} ${secondYear}`;

  const leftLabel = monthLabelOverride ?? computedMonthLabel;
  const rightLabel = secondMonthLabelOverride ?? computedSecondMonthLabel;

  const singleRows = buildCalendarGrid(displayYear, displayMonth, {
    selectedDate: controlledSelected,
    rangeStart: controlledRangeStart,
    rangeEnd: controlledRangeEnd,
    minDate,
    maxDate,
  });

  const rangeLeftRows = range
    ? buildCalendarGrid(displayYear, displayMonth, {
        rangeStart: controlledRangeStart,
        rangeEnd: controlledRangeEnd,
        minDate,
        maxDate,
      })
    : [];

  const rangeRightRows = range
    ? buildCalendarGrid(secondYear, secondMonth, {
        rangeStart: controlledRangeStart,
        rangeEnd: controlledRangeEnd,
        minDate,
        maxDate,
      })
    : [];

  const rootClasses = [
    'spark-date-picker-menu',
    range ? 'spark-date-picker-menu-range' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClasses} role="dialog" aria-label="Date picker">
      {showPreset ? (
        <div className="spark-date-picker-presets">
          <DatePickerPreset
            label="Yesterday"
            onSelect={onPresetSelect}
          />
          <DatePickerPreset
            label="Last Week"
            onSelect={onPresetSelect}
          />
          <DatePickerPreset
            label="Last Month"
            onSelect={onPresetSelect}
          />
        </div>
      ) : null}
      {range ? (
        <div className="spark-date-picker-body-range">
          <CalendarMonth
            monthLabel={leftLabel}
            rows={rangeLeftRows}
            showTrailingNav={false}
            onDayClick={handleDayClick}
            onPrevMonth={goToPrevMonth}
            onPrevYear={goToPrevYear}
            onKeyDown={handleKeyDown}
            gridRef={gridRef}
          />
          <CalendarMonth
            monthLabel={rightLabel}
            rows={rangeRightRows}
            showLeadingNav={false}
            onDayClick={handleDayClick}
            onNextMonth={goToNextMonth}
            onNextYear={goToNextYear}
            onKeyDown={handleKeyDown}
          />
        </div>
      ) : (
        <div className="spark-date-picker-body">
          <CalendarMonth
            monthLabel={leftLabel}
            rows={singleRows}
            onDayClick={handleDayClick}
            onPrevMonth={goToPrevMonth}
            onPrevYear={goToPrevYear}
            onNextMonth={goToNextMonth}
            onNextYear={goToNextYear}
            onKeyDown={handleKeyDown}
            gridRef={gridRef}
          />
          <div className="spark-date-picker-footer">
            <Button variant="ghost" size="small" onClick={handleGoToToday}>
              Today
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
